import { Chip, Collapse, Icon, IconButton, Paper, Typography } from '@shared/ui';
import { useState } from 'react';
import { registerWidgetRenderer } from '../registry';
import type { ThinkingModeEntity, TimelineWidgetProps } from '../types';

export interface ThinkingModeWidgetProps extends TimelineWidgetProps<ThinkingModeEntity> {
  debugMode?: boolean;
}

// Register renderer + visibility
registerWidgetRenderer(
  'thinking_mode',
  ({ entity, debugMode }) => (
    <ThinkingModeWidget key={(entity as any).id} entity={entity as any} debugMode={debugMode} />
  ),
  { visibility: { normal: true, debug: true } }
);

export function ThinkingModeWidget({ entity, debugMode = false }: ThinkingModeWidgetProps) {
  const { status, mode, phase, reasoning, extraData } = entity.props;
  const [expanded, setExpanded] = useState(true);

  const statusColor = status === 'completed' ? 'success' : status === 'error' ? 'error' : 'primary';
  const modeLabel = mode || 'unknown';
  const phaseLabel = phase || 'active';

  return (
    <Paper variant="outlined" className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50">
      <div className="flex items-start gap-3">
        <Icon sx={{ fontSize: 24, color: `${statusColor}.main` }}>
          {status === 'active'
            ? 'psychology'
            : status === 'completed'
              ? 'check_circle'
              : 'error_outline'}
        </Icon>

        <div className="flex-1">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => setExpanded(!expanded)}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              🧠 Thinking Mode
            </Typography>
            {mode ? (
              <Chip label={modeLabel} size="small" color="primary" variant="outlined" />
            ) : null}
            {phase ? <Chip label={phaseLabel} size="small" variant="outlined" /> : null}
            <IconButton size="small" sx={{ ml: 'auto' }}>
              <Icon sx={{ fontSize: 18 }}>{expanded ? 'expand_less' : 'expand_more'}</Icon>
            </IconButton>
          </div>

          <Collapse in={expanded}>
            {status === 'active' || status === 'completed' ? (
              <div className="mt-3 space-y-3">
                {reasoning ? (
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <Typography variant="subtitle2" className="mb-1">
                      Reasoning
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {reasoning}
                    </Typography>
                  </div>
                ) : null}

                {extraData && Object.keys(extraData).length > 0 ? (
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <Typography variant="subtitle2" className="mb-2">
                      Additional Context
                    </Typography>
                    <ul className="space-y-1">
                      {Object.entries(extraData).map(([key, value]) => (
                        <li key={key} className="text-sm text-gray-700 flex items-start gap-2">
                          <span className="text-purple-500 mt-1">•</span>
                          <span>
                            <strong>{key}:</strong>{' '}
                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {status === 'completed' && entity.props.error ? (
                  <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                    <Typography variant="body2" color="error">
                      Error: {entity.props.error}
                    </Typography>
                  </div>
                ) : null}

                {debugMode ? (
                  <details className="bg-gray-100 rounded p-2">
                    <summary className="cursor-pointer text-xs font-mono">Debug Info</summary>
                    <pre className="text-xs mt-2 overflow-auto max-h-48">
                      {JSON.stringify(entity.props, null, 2)}
                    </pre>
                  </details>
                ) : null}
              </div>
            ) : null}

            {status === 'error' ? (
              <div className="mt-3">
                <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                  <Typography variant="body2" color="error">
                    {entity.props.error || 'An error occurred while processing thinking mode'}
                  </Typography>
                </div>
                {debugMode ? (
                  <details className="bg-gray-100 rounded p-2 mt-2">
                    <summary className="cursor-pointer text-xs font-mono">Debug Info</summary>
                    <pre className="text-xs mt-2 overflow-auto max-h-48">
                      {JSON.stringify(entity.props, null, 2)}
                    </pre>
                  </details>
                ) : null}
              </div>
            ) : null}
          </Collapse>
        </div>
      </div>
    </Paper>
  );
}
